import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, User, ArrowRight, Loader2, BookOpen } from 'lucide-react';
import { fetchBlogs } from '../services/api';

const CATEGORIES = ['All', 'Exam Tips', 'Study Methods', 'Motivation', 'Subject Guides'];

const CAT_STYLE = {
  'Exam Tips':      'bg-blue-500/10 text-blue-400 border-blue-500/20',
  'Study Methods':  'bg-green-500/10 text-green-400 border-green-500/20',
  'Motivation':     'bg-purple-500/10 text-purple-400 border-purple-500/20',
  'Subject Guides': 'bg-orange-500/10 text-orange-400 border-orange-500/20',
};

const stagger = { show: { transition: { staggerChildren: 0.08 } } };
const cardAnim = { hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0, transition: { duration: 0.4 } } };

const BlogPage = () => {
  const navigate = useNavigate();
  const [blogs,           setBlogs]           = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [activeCategory,  setActiveCategory]  = useState('All');

  useEffect(() => {
    fetchBlogs()
      .then(res => setBlogs(res.data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const filtered = activeCategory === 'All'
    ? blogs
    : blogs.filter(b => b.category === activeCategory);

  const fmtDate = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  return (
    <div className="min-h-screen">

      {/* ── Hero ── */}
      <section className="relative py-16 px-6 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-1/3 w-72 h-72 bg-accentBlue/6 rounded-full blur-[100px]" />
          <div className="absolute bottom-0 left-1/4 w-60 h-60 bg-purple-500/5 rounded-full blur-[80px]" />
        </div>
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55 }}>
            <p className="text-accentBlue text-xs font-bold uppercase tracking-widest mb-3">Student Blog</p>
            <h1 className="text-4xl sm:text-5xl font-extrabold text-white tracking-tight mb-3">
              Study Tips &{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-accentBlue to-blue-300">Insights</span>
            </h1>
            <p className="text-gray-400 max-w-lg">Latest tips, exam strategies and motivation to help you succeed.</p>
          </motion.div>
        </div>
      </section>

      {/* ── Category filter ── */}
      <div className="max-w-7xl mx-auto px-6 mb-10">
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map(cat => (
            <button key={cat} onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 rounded-xl text-xs font-bold border transition-all ${
                activeCategory === cat
                  ? 'bg-accentBlue/10 border-accentBlue/30 text-accentBlue'
                  : 'bg-white/5 border-white/10 text-gray-400 hover:border-white/20 hover:text-white'
              }`}>
              {cat}
              {cat !== 'All' && (
                <span className="ml-1.5 opacity-60">
                  ({blogs.filter(b => b.category === cat).length})
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* ── Grid ── */}
      <div className="max-w-7xl mx-auto px-6 pb-24">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-32">
            <Loader2 size={32} className="animate-spin text-accentBlue mb-4" />
            <p className="text-gray-500 text-sm">Loading posts…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-32 border border-dashed border-white/10 rounded-2xl text-center">
            <BookOpen size={40} className="text-gray-700 mb-4" />
            <p className="text-white font-bold mb-1">No posts yet</p>
            <p className="text-gray-500 text-sm">{activeCategory === 'All' ? 'No blog posts have been published.' : `No posts in "${activeCategory}" yet.`}</p>
          </div>
        ) : (
          <motion.div variants={stagger} initial="hidden" animate="show"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
            {filtered.map(blog => (
              <motion.div key={blog._id} variants={cardAnim}
                whileHover={{ y: -6 }}
                onClick={() => navigate(`/blog/${blog._id}`)}
                className="group bg-darkBg border border-white/10 rounded-2xl overflow-hidden cursor-pointer hover:border-accentBlue/25 hover:shadow-xl hover:shadow-black/20 transition-all duration-300 flex flex-col">

                {/* Thumbnail */}
                <div className="h-52 bg-white/5 relative overflow-hidden shrink-0">
                  {blog.thumbnail ? (
                    <img src={blog.thumbnail} alt={blog.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <BookOpen size={40} className="text-gray-700" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                  <span className={`absolute top-4 left-4 text-[10px] font-bold px-2.5 py-1 rounded-full border ${CAT_STYLE[blog.category] || 'bg-white/10 text-gray-300 border-white/20'}`}>
                    {blog.category}
                  </span>
                </div>

                {/* Body */}
                <div className="p-5 flex flex-col flex-1">
                  <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
                    <span className="flex items-center gap-1.5"><Calendar size={11} /> {fmtDate(blog.postedDate || blog.createdAt)}</span>
                    <span className="flex items-center gap-1.5"><User size={11} /> {blog.author || 'Admin'}</span>
                  </div>

                  <h3 className="text-base font-bold text-white mb-2 line-clamp-2 group-hover:text-accentBlue transition-colors leading-snug">
                    {blog.title}
                  </h3>
                  <p className="text-xs text-gray-400 line-clamp-3 mb-4 leading-relaxed flex-1">{blog.description}</p>

                  {(blog.tags || []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {blog.tags.slice(0, 3).map(t => (
                        <span key={t} className="text-[10px] px-2 py-0.5 bg-white/8 border border-white/10 text-gray-400 rounded-full">{t}</span>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center gap-1.5 text-accentBlue text-xs font-bold group-hover:gap-3 transition-all pt-3 border-t border-white/8 mt-auto">
                    Read Article <ArrowRight size={13} />
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default BlogPage;
